import React from "react";
import PropTypes from "prop-types";
import {
  Dispatcher,
  NavigatorAction,
  BookUtils,
  BookStore
} from "../../../../flux";
import PinButton from "./start-end/pin-button";

export default class StartEnd extends React.Component {
  constructor(props) {
    super(props);
    const reading = this.props.reading;
    const state = {
      ready: false,
      targetPin: null,
      pages: [],
      start: reading.start,
      end: reading.end,
      summary: reading.summary,
      description: reading.description,
      isCustomDescription: false,
      order: reading.order || 1,
      type: reading.type || this.props.book.format,
      saving: false
    };
    this.state = Object.assign(state, this.props.savedState);
    this._setTargetPin = this._setTargetPin.bind(this);
  }

  componentWillUnmount() {
    Dispatcher.unbind(Dispatcher.BOOK_CLICK, "start-end-book-click");
    Dispatcher.unbind(
      Dispatcher.BOOK_HIGHLIGHT_CLICK,
      "start-end-highlight-click"
    );
    Dispatcher.unbind(Dispatcher.BOOK_PAGE_READY, "start-end-book-page-ready");
    Fraction.Viewer.Viewport.resetCursor();
    this.props.clearPins();
    Dispatcher.emit(Dispatcher.UPDATE_FRACTION_STORE, {
      highlightClickingDisabled: false
    });
  }

  componentDidMount() {
    Dispatcher.bind(
      Dispatcher.BOOK_CLICK,
      this._bookClick.bind(this),
      "start-end-book-click"
    );
    Dispatcher.bind(
      Dispatcher.BOOK_HIGHLIGHT_CLICK,
      (isbn, e, highlight) => {
        const coords = {
          x: highlight.position.left + highlight.position.width / 2,
          y: highlight.position.top + highlight.position.height / 2,
          nodeRect: highlight.position
        };
        this._bookClick(isbn, e, coords);
      },
      "start-end-highlight-click"
    );

    if (this.state.start) {
      this.props.book.viewer.Book.goToCFI(
        this.state.start,
        this.dropStartEndPins.bind(this)
      );
    }
    Dispatcher.bind(
      Dispatcher.BOOK_PAGE_READY,
      this.dropStartEndPins.bind(this),
      "start-end-book-page-ready"
    );
    this.focusOnMount.focus();
  }

  back() {
    NavigatorAction.pop();
    const saved = {};
    saved[this.props.route] = null;
    this.props.saveState(saved);
  }

  dropStartPin(response) {
    if (response.code !== 500) {
      this.props.dropPin("start", response.data.nodeRect, this.state.start);
    }
  }

  dropEndPin(response) {
    const { start, end } = this.state;
    if (response.code !== 500) {
      this.props.dropPin("end", response.data.nodeRect, end);
    }
    const pages = BookUtils.getPageRangeFromCFIs(this.props.isbn, start, end);
    this.setState({ pages });
  }

  dropStartEndPins() {
    const { start, end, targetPin } = this.state;
    if (start) {
      this.props.book.viewer.Book.getPositionForCFI(
        start,
        this.dropStartPin.bind(this)
      );
    }
    if (end) {
      this.props.book.viewer.Book.getPositionForCFI(
        end,
        this.dropEndPin.bind(this)
      );
    }
    this._changeCursor(targetPin);
  }

  _bookClick(isbn, data, coordinates) {
    const book = BookStore.get(isbn);
    coordinates.x = coordinates.nodeRect.left + coordinates.nodeRect.width / 2;
    if (coordinates.nodeRect.height > 35) {
      if (this.state.targetPin === "start") {
        coordinates.y += 10;
      } else if (this.state.targetPin === "end") {
        coordinates.y -= 10;
      }
    }
    book.viewer.Book.getCFIForPoint(
      { x: coordinates.x, y: coordinates.y },
      response => {
        if (response.isSuccess()) {
          const nodeInformation = response.data;
          if (this.state.targetPin) {
            const cfi = `${this.props.book.pageCFI}!${nodeInformation.cfi}`;
            const dropped = this.props.dropPin(
              this.state.targetPin,
              nodeInformation.nodeRect,
              cfi
            );
            if (dropped) {
              if (
                this.state.targetPin === "end" &&
                this.props.book.hasPageBreaks
              ) {
                const pages = BookUtils.getPageRangeFromCFIs(
                  this.props.isbn,
                  this.state.start,
                  cfi
                );
                if (pages) {
                  let description = this.state.description;
                  if (
                    pages &&
                    (!this.state.isCustomDescription || !description)
                  ) {
                    if (pages && pages.length > 1) {
                      description = `Read pages ${pages[0].label} to ${
                        pages.slice(-1).pop().label
                      }`;
                    } else if (pages) {
                      description = `Read page ${pages[0].label}`;
                    }
                  }
                  this.setState({ pages, description });
                }
              }
              let newState = { targetPin: "" };
              newState[this.state.targetPin] = cfi;
              this.setState(newState, () => {
                this.setState(
                  {
                    ready: this.state.start && this.state.end
                  },
                  () => {
                    let newState = {};
                    newState[this.props.route] = this.state;
                    this.state.ready && this.nameInput.focus();
                    this.props.saveState(newState);
                    Dispatcher.emit(Dispatcher.UPDATE_FRACTION_STORE, {
                      highlightClickingDisabled: false
                    });
                  }
                );
              });
            }
          }
        }
      }
    );
  }

  _createReading() {
    if (this.state.ready) {
      const meta = [{ tooling: "start-end" }];
      if (this.props.assignment.uuid) {
        const startCfi = this.state.start;
        const saveReading = this._createOrUpdateReading.bind(this, meta);
        this.setState({ saving: true });
        this.props.book.goTo({
          cfi: this.state.start,
          callback: () => {
            if (startCfi.indexOf("!") === -1) {
              this._savePreview("/4", saveReading);
            } else {
              this._savePreview(startCfi.split("!").pop(), saveReading);
            }
          }
        });
        Fraction.Viewer.Viewport.resetCursor();
      } else {
        this.props.createOrUpdateAssignment().then(() => {
          Fraction.Viewer.Viewport.resetCursor();
          this._createReading(meta);
        });
      }
    }
  }

  _createOrUpdateReading(meta) {
    if (!this.props.saving && this.props.feenyReady) {
      const reading = {
        start: this.state.start,
        end: this.state.end,
        summary: this.state.summary,
        description:
          this.state.description.trim().substring(0, 255) || undefined,
        order: this.state.order,
        type: this.state.type || "ebook",
        exclusive: false,
        range_type: "cfi"
      };
      if (this.props.reading && this.props.reading.uuid) {
        this.props.updateReading(this.props.reading.uuid, reading);
      } else {
        this.props.createReading(reading, meta);
      }
      this.back();
    }
  }

  _savePreview(cfi, callback) {
    this.props.book.viewer.Book.getPreview(cfi, response => {
      this.setState({ saving: false });
      if (response.data) {
        this.setState(
          {
            summary: response.data.preview
          },
          () => {
            const newState = {};
            newState[this.props.route] = this.state;
            this.props.saveState(newState);
            if (typeof callback === "function") {
              callback();
            }
          }
        );
      }
    });
  }

  _changeCursor(pin) {
    if (pin) {
      const pinUrl = `url('${
        window.location.origin
      }/img/assignments/assets/img/assignments_${pin}_pin.png')`;
      const pinUrl2x = `url('${
        window.location.origin
      }/img/assignments/assets/img/assignments_${pin}_pin_2x.png')`;
      const retina = `-webkit-image-set(${pinUrl} 1x, ${pinUrl2x} 2x) 10.5 26`;
      Fraction.Viewer.Viewport.setCursor({ retina, fallback: pinUrl });
    }
  }

  _setTargetPin(pin) {
    Dispatcher.emit(Dispatcher.UPDATE_FRACTION_STORE, {
      highlightClickingDisabled: true
    });
    let newState = { targetPin: pin };
    newState[pin] = "";
    this.setState(newState, () => {
      let newState = {};
      newState[this.props.route] = this.state;
      this.props.saveState(newState);
    });
    if (pin === "start") {
      this.props.clearStartPin();
    } else if (pin === "end") {
      this.props.clearEndPin();
    }
    this._changeCursor(pin);
  }

  _resetPins() {
    Dispatcher.emit(Dispatcher.UPDATE_FRACTION_STORE, {
      highlightClickingDisabled: false
    });
    this.props.clearPins();
    Fraction.Viewer.Viewport.resetCursor();
    const isCustomDescription = this.state.isCustomDescription;
    this.setState(
      {
        start: "",
        end: "",
        pages: [],
        targetPin: null,
        description: isCustomDescription ? this.state.description : ""
      },
      () => {
        const newState = {};
        newState[this.props.route] = this.state;
        this.props.saveState(newState);
      }
    );
  }

  _onDescriptionChange(description) {
    const ready = this.state.start && this.state.end;
    this.setState(
      {
        isCustomDescription: true,
        description: description.substring(0, 255),
        ready
      },
      () => {
        const newState = {};
        newState[this.props.route] = this.state;
        this.props.saveState(newState);
      }
    );
  }

  renderPinButton(pin) {
    if (this.state.targetPin === pin) {
      return <PinButton />;
    }
    if (this.state[pin]) {
      return (
        <PinButton move={true} pin={pin} setTargetPin={this._setTargetPin} />
      );
    }
    return (
      <PinButton focus={true} pin={pin} setTargetPin={this._setTargetPin} />
    );
  }

  renderSummary() {
    if (this.state.pages && this.state.pages.length) {
      const pages = this.state.pages;
      return (
        <div className="page-range-summary">
          <span>
            {i18n.t("assignments.n-pages", {
              pages: (pages && pages.length) || 0
            })}{" "}
            {i18n.t("assignments.in-selection")}
          </span>
          <button
            tabIndex={0}
            className="noButton resetButton"
            onClick={this._resetPins.bind(this)}
          >
            {i18n.t("assignments.reset-pins")}
          </button>
        </div>
      );
    }
    if (this.state.start && this.state.end) {
      return (
        <div className="page-range-summary">
          <button
            className="noButton resetButton"
            onClick={this._resetPins.bind(this)}
          >
            {i18n.t("assignments.reset-pins")}
          </button>
        </div>
      );
    }
  }

  render() {
    const placeholderExample = i18n.t("assignments.start-end-ie");

    return (
      <div id="reading-new">
        <div className="instructions">
          <h3
            className="title"
            ref={input => {
              this.focusOnMount = input;
            }}
            tabIndex={-1}
          >
            {i18n.t("assignments.start-end")}
          </h3>
          <p>{i18n.t("assignments.start-end-desc")}</p>
        </div>
        <div className="reading-form">
          <div className="pins-container">
            {this.renderPinButton("start")}
            {(this.state.end || this.state.start) &&
              this.renderPinButton("end")}
            {this.renderSummary()}
          </div>
          <div className="input-container">
            <label htmlFor="reading-name" className="label">
              {i18n.t("assignments.reading-name")} (
              {i18n.t("base.optional").toLowerCase()})
            </label>
            <input
              ref={input => {
                this.nameInput = input;
              }}
              className="input"
              type="text"
              placeholder={placeholderExample}
              name="reading-name"
              id="reading-name"
              value={this.state.description}
              maxLength={255}
              onChange={event => this._onDescriptionChange(event.target.value)}
            />
            <div className="form-actions">
              <button
                className={`${
                  this.state.ready && !this.state.saving
                    ? "submit"
                    : "transparentButton"
                }`}
                disabled={`${
                  !this.state.ready && this.state.saving ? "disabled" : ""
                }`}
                onClick={this._createReading.bind(this)}
              >
                {this.state.saving
                  ? `${i18n.t("base.saving")}...`
                  : i18n.t("base.save")}
              </button>
              <button className="cancel noButton" onClick={() => this.back()}>
                {i18n.t("base.cancel")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

StartEnd.propTypes = {
  isbn: PropTypes.string.isRequired,
  book: PropTypes.object.isRequired,
  saving: PropTypes.bool.isRequired,
  createReading: PropTypes.func.isRequired,
  createOrUpdateAssignment: PropTypes.func.isRequired,
  assignment: PropTypes.object,
  reading: PropTypes.object
};

StartEnd.defaultProps = {
  reading: {}
};
